import React from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { FaTrash, FaEdit, FaMapMarkerAlt, FaClock, FaPhone, FaWhatsapp } from 'react-icons/fa';
import { API_URL } from '../../config';

const ListContainer = styled.div`
  margin-top: 2rem;
`;

const BranchCard = styled(motion.div)`
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 1rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  position: relative;
`;

const BranchHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1rem;
`;

const BranchInfo = styled.div`
  flex: 1;
`;

const BranchName = styled.h3`
  color: ${props => props.theme.colors.primary};
  font-size: 1.3rem;
  margin-bottom: 0.5rem;
`;

const Location = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: ${props => props.theme.colors.text.secondary};
  font-size: 0.9rem;
  margin-bottom: 1rem;

  svg {
    color: ${props => props.theme.colors.secondary};
  }
`;

const Actions = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const ActionButton = styled.button`
  padding: 0.5rem;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  background: ${props => props.variant === 'danger' ? '#ff4444' : props.theme.colors.primary};
  color: white;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }
`;

const ServicesList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1rem;
  margin-top: 1rem;
`;

const ServiceCard = styled.div`
  background: ${props => props.theme.colors.background.light};
  padding: 1rem;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const ServiceInfo = styled.div`
  flex: 1;
`;

const ServiceName = styled.h4`
  color: ${props => props.theme.colors.text.primary};
  font-size: 1rem;
  margin-bottom: 0.25rem;
`;

const ServiceTime = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: ${props => props.theme.colors.text.secondary};
  font-size: 0.9rem;

  svg {
    color: ${props => props.theme.colors.secondary};
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 2rem;
  color: ${props => props.theme.colors.text.secondary};
`;

const BranchList = ({ branches = [], onEdit, onDelete, onRefresh }) => {
  const handleDelete = async (branchId) => {
    if (!window.confirm('Are you sure you want to delete this branch?')) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/home-content/branches/${branchId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to delete branch');
      }

      if (onRefresh) onRefresh();
    } catch (error) {
      console.error('Error deleting branch:', error);
      alert('Failed to delete branch');
    }
  };

  return (
    <ListContainer>
      <AnimatePresence>
        {branches.length > 0 ? (
          branches.map((branch) => (
            <BranchCard
              key={branch._id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              layout
            >
              <BranchHeader>
                <BranchInfo>
                  <BranchName>{branch.name}</BranchName>
                  <Location>
                    <FaMapMarkerAlt />
                    {branch.location}
                  </Location>
                  {branch.phone && (
                    <Location>
                      <FaPhone />
                      {branch.phone}
                    </Location>
                  )}
                  <Location>
                    <FaWhatsapp />
                    WhatsApp: {branch.hasWhatsApp ? 'Enabled' : 'Disabled'}
                    {branch.hasWhatsApp && branch.whatsappNumber && ` (${branch.whatsappNumber})`}
                  </Location>
                </BranchInfo>
                <Actions>
                  <ActionButton onClick={() => onEdit(branch)}>
                    <FaEdit />
                  </ActionButton>
                  <ActionButton 
                    variant="danger"
                    onClick={() => handleDelete(branch._id)}
                  >
                    <FaTrash />
                  </ActionButton>
                </Actions>
              </BranchHeader>

              <ServicesList>
                {branch.services?.map((service, index) => (
                  <ServiceCard key={index}>
                    <ServiceInfo>
                      <ServiceName>{service.name}</ServiceName>
                      <ServiceTime>
                        <FaClock />
                        {service.time}
                      </ServiceTime>
                    </ServiceInfo>
                  </ServiceCard>
                ))}
              </ServicesList>
            </BranchCard>
          ))
        ) : (
          <EmptyState>No branches found</EmptyState>
        )}
      </AnimatePresence>
    </ListContainer>
  );
};

export default BranchList; 